/**
 * `<VersionDiffModal>` — side-by-side comparison of two snapshots
 * (`external-sharing` redesign 2026-05-10).
 *
 * Layout (top → bottom):
 * 1. Header — title + recipient picker dropdown (when multiple recipients) + close.
 * 2. KEY METRICS strip — IRR / DSCR / EM / CoC tiles, before → after with delta.
 * 3. Side-by-side inputs panel — two columns "You sent" / "They have", grouped
 *    into 7 buckets (Property / Acquisition / Construction / Financing /
 *    Income / Operating / Returns / Other) by heuristic name-pattern match.
 *    Each row shows name | left value | right value, color-coded:
 *      changed → text-textBright on the side that diverged
 *      added   → only right column has a value (left "—")
 *      removed → only left column has a value (right "—")
 *      unchanged → omitted entirely (only diff'd rows render)
 *
 * Driven by `useVersionDiff({from, to})` against `GET /api/shares/diff`.
 * Mount-gate split mirrors `<VersionGraphModal>`: outer wrapper early-returns
 * `null` when closed so the inner `useVersionDiff` hook never runs.
 *
 * Recipient picker: when the parent passes `recipients` with length > 1,
 * a `<select>` renders in the header. Switching invokes `onSelectRecipient`
 * which the parent uses to re-fire the diff with the new (from, to) pair.
 */
import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiError } from '@/lib/api';
import { useVersionDiff } from '@/hooks/use-version-diff';
import { formatNumber } from '@/lib/format';
import type { DiffSide, VersionDiffResponse, VersionGraphChildShare } from '@arcanum-hq/shared-types';

const UNCATEGORIZED = 'Other';

interface UnifiedRow {
  name: string;
  category: string;
  displayName?: string;
  oldValue: unknown;
  newValue: unknown;
  oldSource?: string;
  newSource?: string;
  status: 'changed' | 'added' | 'removed';
}

function buildUnifiedRows(inputs: VersionDiffResponse['inputs']): UnifiedRow[] {
  const rows: UnifiedRow[] = [];
  for (const c of inputs.changed) {
    rows.push({
      name: c.name,
      category: c.category ?? UNCATEGORIZED,
      displayName: c.displayName,
      oldValue: c.oldValue,
      newValue: c.newValue,
      oldSource: c.oldSource,
      newSource: c.newSource,
      status: 'changed',
    });
  }
  for (const a of inputs.added) {
    rows.push({
      name: a.name,
      category: a.category ?? UNCATEGORIZED,
      displayName: a.displayName,
      oldValue: undefined,
      newValue: a.value,
      newSource: a.source,
      status: 'added',
    });
  }
  for (const r of inputs.removed) {
    rows.push({
      name: r.name,
      category: r.category ?? UNCATEGORIZED,
      displayName: r.displayName,
      oldValue: r.value,
      newValue: undefined,
      oldSource: r.source,
      status: 'removed',
    });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name));
  return rows;
}

function groupBySection(rows: UnifiedRow[]): Map<string, UnifiedRow[]> {
  const buckets = new Map<string, UnifiedRow[]>();
  for (const row of rows) {
    const bucket = buckets.get(row.category);
    if (bucket) bucket.push(row);
    else buckets.set(row.category, [row]);
  }
  return buckets;
}

/**
 * Section render order: schema-supplied `categoryOrder` first (the model
 * author's authoring order), then any categories present in rows but
 * missing from the order list (alphabetical for determinism), then the
 * `UNCATEGORIZED` bucket last so it never floats above named sections.
 * Model-agnostic — no hardcoded section names.
 */
function resolveSectionOrder(
  buckets: Map<string, UnifiedRow[]>,
  schemaOrder: string[] | undefined,
): string[] {
  const present = new Set(buckets.keys());
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const cat of schemaOrder ?? []) {
    if (present.has(cat) && !seen.has(cat)) {
      ordered.push(cat);
      seen.add(cat);
    }
  }
  const remaining = [...present].filter((c) => !seen.has(c) && c !== UNCATEGORIZED).sort();
  ordered.push(...remaining);
  if (present.has(UNCATEGORIZED)) ordered.push(UNCATEGORIZED);
  return ordered;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '—';
    return formatNumber(v);
  }
  if (typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function formatDeltaPct(pct: number): string {
  if (!Number.isFinite(pct)) return '—';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

export interface VersionDiffModalProps {
  open: boolean;
  from: DiffSide | null;
  to: DiffSide | null;
  /** Header label for the "from" side (e.g. parent conversation title). */
  fromLabel?: string;
  /** Header label for the "to" side (e.g. fork conversation title). */
  toLabel?: string;
  /** Recipient list — when length > 1, a picker renders in the header. */
  recipients?: VersionGraphChildShare[];
  /** Currently selected share id (anchor for the picker). */
  selectedShareId?: string | null;
  /** Fired when the user picks a different recipient from the dropdown. */
  onSelectRecipient?: (shareId: string) => void;
  onClose: () => void;
}

export function VersionDiffModal(props: VersionDiffModalProps) {
  if (!props.open) return null;
  return <VersionDiffModalBody {...props} />;
}

function VersionDiffModalBody({
  from,
  to,
  fromLabel,
  toLabel,
  recipients,
  selectedShareId,
  onSelectRecipient,
  onClose,
}: VersionDiffModalProps) {
  const query = useVersionDiff({ from, to });

  useEffect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, [onClose]);

  const data = query.data;
  const inputs = data?.inputs;
  const metrics = data?.metrics ?? [];

  const showPicker = !!recipients && recipients.length > 1 && !!onSelectRecipient;

  const rows = inputs ? buildUnifiedRows(inputs) : [];
  const grouped = groupBySection(rows);
  const populatedSections = resolveSectionOrder(grouped, data?.categoryOrder);

  return (
    <div
      className="fixed inset-0 bg-void/95 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="version-diff-modal-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="version-diff-modal-title"
        data-testid="version-diff-modal"
        className="bg-panelBg border border-panelBorder rounded-[3px] shadow-xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-panelBorder gap-4">
          <div className="flex flex-col gap-1 min-w-0">
            <h2
              id="version-diff-modal-title"
              className="font-mono text-xs text-textBright uppercase tracking-[1px]"
            >
              Version diff
            </h2>
            {(fromLabel || toLabel) && (
              <p className="text-xs font-mono text-text-muted truncate">
                <span>{fromLabel ?? 'You sent'}</span>
                <span className="mx-2 text-text-dim">→</span>
                <span>{toLabel ?? 'They have'}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {showPicker && (
              <label className="flex items-center gap-2 font-mono text-micro uppercase tracking-[0.5px] text-text-muted">
                <span>Recipient:</span>
                <select
                  data-testid="version-diff-recipient-picker"
                  value={selectedShareId ?? ''}
                  onChange={(e) => onSelectRecipient?.(e.target.value)}
                  className="bg-panelBg border border-panelBorder text-textBright font-mono text-xs px-2 py-1 rounded-[2px] focus:outline-none focus:border-accent/40"
                >
                  {recipients!.map((r) => (
                    <option key={r.shareId} value={r.shareId}>
                      {r.recipientName} ({r.status})
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Button variant="secondary" onClick={onClose} className="text-micro">
              Close
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {query.isLoading ? (
            <div data-testid="version-diff-loading" className="flex flex-col gap-3 px-5 py-4">
              <Skeleton variant="block" className="h-12" />
              <Skeleton variant="block" className="h-32" />
              <Skeleton variant="block" className="h-32" />
            </div>
          ) : query.isError ? (
            <div className="px-5 py-4">
              <EmptyState
                title={
                  query.error instanceof ApiError && query.error.status === 404
                    ? 'DIFF NOT AVAILABLE'
                    : "COULDN'T LOAD DIFF"
                }
                description={
                  query.error instanceof ApiError && query.error.status === 404
                    ? "One of these snapshots doesn't exist or you don't have access."
                    : 'Something went wrong fetching the diff. Try again.'
                }
                action={
                  <Button variant="primary" onClick={() => query.refetch()}>
                    Retry
                  </Button>
                }
              />
            </div>
          ) : !inputs ? (
            <div className="px-5 py-4">
              <EmptyState title="NO DIFF DATA" description="The server returned an empty response." />
            </div>
          ) : (
            <>
              {/* KEY METRICS strip */}
              <div
                data-testid="version-diff-key-metrics"
                className="border-b border-panelBorder bg-panelBg/40 px-5 py-4"
              >
                <h3 className="font-mono text-micro text-text-muted uppercase tracking-[0.5px] mb-3">
                  Key metrics
                </h3>
                {metrics.length === 0 ? (
                  <p className="font-mono text-xs text-text-dim italic">
                    No metric deltas available.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {metrics.map((m) => {
                      const finite = Number.isFinite(m.deltaPct);
                      const tone =
                        !finite || m.deltaPct === 0
                          ? 'text-text-muted'
                          : m.deltaPct > 0
                            ? 'text-accent'
                            : 'text-warn';
                      return (
                        <div
                          key={m.name}
                          data-testid={`version-diff-metric-${m.name}`}
                          className="border border-panelBorder rounded-[2px] bg-panelBg p-3 flex flex-col gap-1"
                        >
                          <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
                            {m.displayName ?? m.name}
                          </span>
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-mono text-xs text-text-muted tabular-nums">
                              {formatValue(m.oldValue)}
                            </span>
                            <span className="text-text-dim">→</span>
                            <span className="font-mono text-sm text-textBright tabular-nums">
                              {formatValue(m.newValue)}
                            </span>
                          </div>
                          <span className={`font-mono text-micro tabular-nums ${tone}`}>
                            {formatDeltaPct(m.deltaPct)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Side-by-side inputs panel */}
              <div className="px-5 py-4">
                {/* Column headers */}
                <div
                  className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr] gap-4 border-b border-panelBorder pb-2 mb-3 sticky top-0 bg-panelBg z-10"
                  data-testid="version-diff-column-headers"
                >
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
                    Field
                  </span>
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
                    You sent
                  </span>
                  <span className="font-mono text-micro text-textBright uppercase tracking-[0.5px]">
                    They have
                  </span>
                  <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
                    Source
                  </span>
                </div>

                {populatedSections.length === 0 ? (
                  <EmptyState
                    title="NO INPUT CHANGES"
                    description="Their version has the same inputs as what you sent."
                  />
                ) : (
                  populatedSections.map((section) => {
                    const sectionRows = grouped.get(section) ?? [];
                    // Build a testid-safe slug: `Returns` → `returns`,
                    // `Financing - Acq` → `financing-acq`. Schema-author-
                    // controlled section names → predictable testids.
                    const slug = section
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-|-$/g, '');
                    return (
                      <div
                        key={section}
                        data-testid={`version-diff-section-${slug}`}
                        className="mb-5 last:mb-0"
                      >
                        <h4 className="font-mono text-micro text-accent uppercase tracking-[1px] mb-2">
                          {section}
                          <span className="ml-2 text-text-dim">({sectionRows.length})</span>
                        </h4>
                        <div className="flex flex-col">
                          {sectionRows.map((row) => (
                            <div
                              key={row.name}
                              data-testid={`version-diff-row-${row.name}`}
                              data-row-status={row.status}
                              className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr] gap-4 py-2 border-t border-panelBorder/40 first:border-t-0 items-baseline"
                            >
                              <span
                                className="font-mono text-xs text-textBright break-all"
                                title={row.name}
                              >
                                {row.displayName ?? row.name}
                              </span>
                              <span
                                className={`font-mono text-xs tabular-nums break-all ${
                                  row.status === 'added'
                                    ? 'text-text-dim'
                                    : row.status === 'removed'
                                      ? 'text-warn line-through'
                                      : 'text-text-muted'
                                }`}
                              >
                                {formatValue(row.oldValue)}
                              </span>
                              <span
                                className={`font-mono text-xs tabular-nums break-all ${
                                  row.status === 'removed'
                                    ? 'text-text-dim'
                                    : row.status === 'added'
                                      ? 'text-accent'
                                      : 'text-textBright'
                                }`}
                              >
                                {formatValue(row.newValue)}
                              </span>
                              <span className="font-mono text-micro text-text-muted">
                                {row.oldSource && row.newSource && row.oldSource !== row.newSource ? (
                                  <>
                                    <span className="text-text-dim">{row.oldSource}</span>
                                    <span className="mx-1 text-text-dim">→</span>
                                    <span>{row.newSource}</span>
                                  </>
                                ) : (
                                  <span className="text-text-dim">
                                    {row.newSource ?? row.oldSource ?? '—'}
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
